import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Collapse,
} from '@mui/material';

const PipeSelectionDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  pipeType, // 'round', 'square-profile', 'square-sheet'
  size, // диаметр или профиль
  availableCoatings, 
  availableThicknesses,
  defaultCoating,
  defaultThickness,
  validCombinations, // Массив валидных комбинаций {coating, thickness}
}) => {
  const [coating, setCoating] = useState(defaultCoating || '');
  const [thickness, setThickness] = useState(defaultThickness || '');
  const [warning, setWarning] = useState({ show: false, message: '', type: 'info' });

  useEffect(() => {
    if (open) {
      setCoating(defaultCoating || (availableCoatings.length > 0 ? availableCoatings[0] : ''));
      setThickness(defaultThickness || (availableThicknesses.length > 0 ? availableThicknesses[0] : ''));
    }
  }, [open, defaultCoating, defaultThickness, availableCoatings, availableThicknesses]);

  // Проверяем, валидна ли текущая комбинация
  const isValidCombination = React.useMemo(() => {
    if (!validCombinations || !coating || !thickness) return true;
    return validCombinations.some(combo => 
      combo.coating === coating && combo.thickness === thickness
    );
  }, [coating, thickness, validCombinations]);

  // Проверяем, доступна ли конкретная толщина для выбранного покрытия
  const isThicknessAvailable = React.useCallback((thick) => {
    if (!validCombinations || !coating) return true;
    return validCombinations.some(combo => 
      combo.coating === coating && combo.thickness === thick
    );
  }, [coating, validCombinations]);


  const handleConfirm = () => {
    onConfirm({ coating, thickness });
    onClose();
  };

  const getTitle = () => {
    if (pipeType === 'round') return `Труба Ø ${size}`;
    if (pipeType === 'square-profile') return `Профиль ${size}`;
    if (pipeType === 'square-sheet') return `Лист ${size}`;
    return 'Выбор параметров';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Покрытие</InputLabel>
            <Select
              value={coating}
              onChange={(e) => {
                const newCoating = e.target.value;
                setCoating(newCoating);
                
                // Автоматически переключаем толщину на доступную для этого покрытия
                if (validCombinations) {
                  const availableThicknessesForCoating = validCombinations
                    .filter(combo => combo.coating === newCoating)
                    .map(combo => combo.thickness);
                  
                  // Если текущая толщина недоступна для нового покрытия
                  if (!availableThicknessesForCoating.includes(thickness)) {
                    // Приоритет: x2 (или 2), затем первая доступная
                    let newThickness = null;
                    if (availableThicknessesForCoating.includes('x2')) {
                      newThickness = 'x2';
                    } else if (availableThicknessesForCoating.includes('2')) {
                      newThickness = '2';
                    } else if (availableThicknessesForCoating.length > 0) {
                      newThickness = availableThicknessesForCoating[0];
                    }
                    
                    if (newThickness) {
                      setThickness(newThickness);
                      // Показываем уведомление об автоматическом переключении
                      setWarning({
                        show: true,
                        message: `Толщина автоматически изменена на "${newThickness}"`,
                        type: 'info'
                      });
                      // Скрываем через 3 секунды
                      setTimeout(() => setWarning({ show: false, message: '', type: 'info' }), 3000);
                    }
                  }
                }
              }}
              label="Покрытие"
            >
              {availableCoatings.map((coat) => (
                <MenuItem key={coat} value={coat}>
                  {coat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Толщина</InputLabel>
            <Select
              value={thickness}
              onChange={(e) => {
                const newThickness = e.target.value;
                // Проверяем, валидна ли комбинация с текущим покрытием
                if (validCombinations && coating) {
                  const isValid = validCombinations.some(combo => 
                    combo.coating === coating && combo.thickness === newThickness
                  );
                  if (!isValid) {
                    setWarning({
                      show: true,
                      message: `Толщина "${newThickness}" недоступна для покрытия "${coating}". Выберите другое покрытие.`,
                      type: 'error'
                    });
                    // Скрываем через 4 секунды
                    setTimeout(() => setWarning({ show: false, message: '', type: 'info' }), 4000);
                    return;
                  }
                }
                setThickness(newThickness);
              }}
              label="Толщина"
            >
              {availableThicknesses.map((thick) => {
                const available = isThicknessAvailable(thick);
                return (
                  <MenuItem 
                    key={thick} 
                    value={thick}
                    sx={{
                      textDecoration: available ? 'none' : 'line-through',
                      color: available ? 'inherit' : '#999',
                      opacity: available ? 1 : 0.6,
                    }}
                  >
                    {thick}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Встроенное предупреждение */}
          <Collapse in={warning.show}>
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity={warning.type}
                onClose={() => setWarning({ show: false, message: '', type: 'info' })}
              >
                {warning.message}
              </Alert>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Отмена
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          variant="contained"
          disabled={!coating || !thickness || !isValidCombination}
        >
          Применить
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default PipeSelectionDialog;
